import React, { useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { format, subDays } from 'date-fns';

import { Transaction } from '../../types/Transaction';
import { useFormatting } from '../../context/FormattingContext';
import { formatUTCDateForCalendar } from '../../utils/dateUtils';
import { getTodayDateKeyInTimeZone } from '../../utils/formatting';
import { CalendarDailyEntry } from '../../hooks/useCalendar';

interface CalendarViewProps {
  events: Transaction[];
  daily?: CalendarDailyEntry[];
  onRangeChange?: (start: string, end: string) => void;
  onEventClick?: (tx: Transaction) => void;
  onDateClick?: (date: string) => void;
  onEventDrop?: (params: { txId: string; newDateISO: string; revert: () => void }) => void;
}

const formatDateKeyUTC = (date: Date): string => {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
    .toISOString()
    .split('T')[0];
};

const formatAmount = (value: number) => {
  const rounded = Math.round(value);
  return `${rounded < 0 ? '-' : ''}${Math.abs(rounded)}`;
};

type CalendarEventVariant =
  | 'income'
  | 'expense'
  | 'savings'
  | 'transfer-in'
  | 'transfer-out'
  | 'transfer-savings-in'
  | 'transfer-savings-out';

const resolveSignedAmount = (tx: Transaction): number => {
  if (typeof tx.displayAmountSigned === 'number') return tx.displayAmountSigned;
  if (typeof tx.amountSigned === 'number') return tx.amountSigned;
  return 0;
};

const resolveEventVariant = (tx: Transaction): CalendarEventVariant => {
  const isDirectionalKind = tx.kind === 'transfer' || tx.kind === 'cardPayment';
  if (!isDirectionalKind) {
    return tx.type === 'income' ? 'income' : tx.type === 'expense' ? 'expense' : 'savings';
  }

  const directionFromType = tx.direction?.fromAccountType || tx.fromAccountType;
  const directionToType = tx.direction?.toAccountType || tx.toAccountType;
  if (directionToType === 'savings') return 'transfer-savings-in';
  if (directionFromType === 'savings') return 'transfer-savings-out';

  return resolveSignedAmount(tx) < 0 ? 'transfer-out' : 'transfer-in';
};

const getPrimaryEventIdentity = (tx: Transaction): string => {
  if (tx.kind === 'transfer') return 'TRANSFER';
  if (tx.kind === 'cardPayment') return 'CARD PAYMENT';
  if (tx.type === 'income') return 'INCOME';
  if (tx.type === 'expense') return 'EXPENSE';
  return 'SAVINGS';
};

const getVariantColor = (variant: CalendarEventVariant, tentative?: boolean): string => {
  const base =
    variant === 'income'
      ? '#2e7d32'
      : variant === 'expense'
        ? '#c62828'
        : variant === 'transfer-out'
          ? '#ef6c00'
          : variant === 'transfer-in'
            ? '#1565c0'
            : variant === 'transfer-savings-in'
              ? '#00695c'
              : variant === 'transfer-savings-out'
                ? '#6d4c41'
                : '#00838f';
  return tentative ? `${base}99` : base;
};

const getVariantBadge = (variant: CalendarEventVariant): string | null => {
  if (variant === 'transfer-in') return 'TRANSFER IN';
  if (variant === 'transfer-out') return 'TRANSFER OUT';
  if (variant === 'transfer-savings-in') return 'SAVINGS IN';
  if (variant === 'transfer-savings-out') return 'SAVINGS OUT';
  return null;
};

const isPaidTransaction = (tx: Transaction): boolean => tx.paymentStatus === 'paid' || Boolean(tx.paidAt);

const resolveTransactionDateKey = (tx: Transaction): string => {
  if (typeof tx.date === 'string' && tx.date.length >= 10) return tx.date.slice(0, 10);
  return formatUTCDateForCalendar(tx.date);
};

const isOverdueTransaction = (tx: Transaction, todayDateKey: string): boolean => {
  if (tx.tentative) return false;
  if (isPaidTransaction(tx)) return false;
  const txDateKey = resolveTransactionDateKey(tx);
  if (!txDateKey || !todayDateKey) return false;
  return txDateKey < todayDateKey;
};

const CalendarView = ({ events, daily = [], onRangeChange, onEventClick, onDateClick, onEventDrop }: CalendarViewProps) => {
  const [calendarView, setCalendarView] = useState<'dayGridMonth' | 'timeGridWeek'>('dayGridMonth');
  const formatting = useFormatting();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('md'));
  const todayDateKey = useMemo(
    () => getTodayDateKeyInTimeZone(formatting.timeZone),
    [formatting.timeZone]
  );

  const dailyMap = useMemo(() => {
    const map = new Map<string, CalendarDailyEntry>();
    daily.forEach((entry) => map.set(entry.date, entry));
    return map;
  }, [daily]);

  const calendarEvents = events.map((tx) => {
    const variant = resolveEventVariant(tx);
    const primaryIdentity = getPrimaryEventIdentity(tx);

    return {
      id: tx._id,
      title: `${primaryIdentity} ${tx.amount}`,
      date: formatUTCDateForCalendar(tx.date),
      backgroundColor: getVariantColor(variant, tx.tentative),
      borderColor: tx.tentative ? '#ffb300' : 'transparent',
      textColor: '#fffae8',
      classNames: [
        tx.tentative ? 'fc-tentative' : 'fc-committed',
        `cal-variant-${variant}`,
        tx.ownerScope ? `scope-${tx.ownerScope}` : 'scope-household',
        tx.visibility ? `visibility-${tx.visibility}` : 'visibility-shared',
      ],
      extendedProps: {
        transaction: tx,
        variant,
      },
    };
  });

  const renderEventContent = (eventInfo: any) => {
    const tx: Transaction = eventInfo.event.extendedProps.transaction;
    const variant: CalendarEventVariant = eventInfo.event.extendedProps.variant;
    const primaryIdentity = getPrimaryEventIdentity(tx);
    const paid = isPaidTransaction(tx);
    const overdue = isOverdueTransaction(tx, todayDateKey);

    const badges = [] as string[];
    if (tx.tentative) badges.push('TENTATIVE');
    if (tx.visibility === 'private') badges.push('PRIVATE');
    if (tx.ownerScope && tx.ownerScope !== 'household') badges.push(tx.ownerScope.toUpperCase());
    const variantBadge = getVariantBadge(variant);
    const showCompactEvent = isTabletOrBelow;
    const showMinimalEvent = isPhone;
    const showCategory = !showMinimalEvent;
    const showVariantBadge = !showCompactEvent && Boolean(variantBadge);
    const showStatusBadges = !showCompactEvent;

    return (
      <div
        style={{
          backgroundColor: eventInfo.event.backgroundColor,
          color: eventInfo.event.textColor,
          padding: showMinimalEvent ? '1px 4px' : showCompactEvent ? '2px 4px' : '2px 6px',
          borderRadius: '6px',
          fontSize: showMinimalEvent ? '0.72rem' : showCompactEvent ? '0.78rem' : '0.85rem',
          lineHeight: showMinimalEvent ? 1.1 : showCompactEvent ? 1.15 : 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
          opacity: paid ? 0.76 : 1,
          border: tx.tentative
            ? '1px dashed #ffb300'
            : overdue
              ? '1px solid #d32f2f'
              : paid
                ? '1px solid #2e7d32'
                : '1px solid transparent',
        }}
        title={`${tx.category}${tx.description ? ` • ${tx.description}` : ''}`}
      >
        <div style={{ fontWeight: 700 }}>{primaryIdentity} ${formatAmount(tx.amount)}</div>
        {showCategory && <div style={{ fontSize: showCompactEvent ? '0.62rem' : '0.7rem', opacity: 0.9 }}>{tx.category}</div>}
        {showVariantBadge && variantBadge && (
          <div data-testid={`cal-event-variant-${variant}`} style={{ fontSize: '0.65rem', opacity: 0.95, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            [{variantBadge}]
          </div>
        )}
        {showStatusBadges && paid && (
          <div data-testid="cal-badge-paid" style={{ fontSize: '0.65rem', opacity: 0.95 }}>
            [PAID]
          </div>
        )}
        {showStatusBadges && overdue && (
          <div data-testid="cal-badge-overdue" style={{ fontSize: '0.65rem', color: '#d32f2f', fontWeight: 700 }}>
            [OVERDUE]
          </div>
        )}
        {showStatusBadges && badges.length > 0 && (
          <div style={{ fontSize: '0.65rem', opacity: 0.9 }}>[{badges.join(' · ')}]</div>
        )}
      </div>
    );
  };

  const renderDayCellContent = (arg: any) => {
    const key = formatDateKeyUTC(arg.date);
    const entry = dailyMap.get(key);
    const showProjected = Boolean(entry && entry.runningProjectedBalance !== entry.runningCommittedBalance);
    const showCompactBalance = isTabletOrBelow;
    const showMinimalBalance = isPhone;
    const committedLabel = showMinimalBalance ? 'C' : showCompactBalance ? 'Comm' : 'Committed';
    const projectedLabel = showMinimalBalance ? 'Proj' : 'Projected';
    const showProjectedLine = showProjected && !showMinimalBalance;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        <div>{arg.dayNumberText}</div>
        {entry && (
          <div
            data-testid={`cal-balance-${key}`}
            style={{
              marginTop: showMinimalBalance ? 2 : 4,
              padding: showMinimalBalance ? '1px 3px' : showCompactBalance ? '2px 3px' : '2px 4px',
              borderRadius: 6,
              backgroundColor: '#f4f6fb',
              fontSize: showMinimalBalance ? '0.56rem' : showCompactBalance ? '0.6rem' : '0.64rem',
              lineHeight: showMinimalBalance ? 1.08 : showCompactBalance ? 1.1 : 1.15,
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <div style={{ color: '#1f3a93', fontWeight: 600, marginBottom: showMinimalBalance ? 1 : 2 }}>
              {showCompactBalance ? 'Bal' : 'Balance'}
            </div>
            <div style={{ color: '#2e7d32', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {committedLabel} {formatAmount(entry.runningCommittedBalance)}
            </div>
            {showProjectedLine && (
              <div style={{ color: entry.runningProjectedBalance < 0 ? '#c62828' : '#261b5e' }}>
                {projectedLabel} {formatAmount(entry.runningProjectedBalance)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.default',
          px: { xs: 1.25, sm: 1.5 },
          py: { xs: 1, sm: 1.25 },
          mb: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Choose a calendar view
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant={calendarView === 'dayGridMonth' ? 'contained' : 'outlined'}
              onClick={() => setCalendarView('dayGridMonth')}
            >
              Month
            </Button>
            <Button
              variant={calendarView === 'timeGridWeek' ? 'contained' : 'outlined'}
              onClick={() => setCalendarView('timeGridWeek')}
            >
              Week
            </Button>
          </Stack>
        </Stack>
      </Box>

      <FullCalendar
        datesSet={(arg) => {
          const isMonthView = arg.view.type === 'dayGridMonth';
          const isWeekView = arg.view.type === 'timeGridWeek' || arg.view.type === 'dayGridWeek';

          let rangeStart = arg.start;
          let rangeEndExclusive = arg.end;

          if (isMonthView) {
            rangeStart = arg.view.currentStart;
            rangeEndExclusive = arg.view.currentEnd;
          } else if (isWeekView) {
            rangeStart = arg.start;
            rangeEndExclusive = arg.end;
          }

          const rangeEndInclusive = subDays(rangeEndExclusive, 1);

          onRangeChange?.(format(rangeStart, 'yyyy-MM-dd'), format(rangeEndInclusive, 'yyyy-MM-dd'));
        }}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={calendarView}
        key={calendarView}
        headerToolbar={{
          left: isPhone ? 'prev,next' : 'prev,next today',
          center: 'title',
          right: isPhone ? 'today' : '',
        }}
        height="auto"
        events={calendarEvents}
        eventContent={renderEventContent}
        dayCellContent={renderDayCellContent}
        eventClick={(clickInfo) => {
          const tx = clickInfo.event.extendedProps.transaction as Transaction;
          onEventClick?.(tx);
        }}
        editable
        eventDrop={(dropInfo) => {
          const txId = String(dropInfo.event.id || '');
          const droppedDate = dropInfo.event.start;
          if (!txId || !droppedDate) {
            dropInfo.revert();
            return;
          }
          onEventDrop?.({
            txId,
            newDateISO: format(droppedDate, 'yyyy-MM-dd'),
            revert: () => dropInfo.revert(),
          });
        }}
        dateClick={(arg) => {
          onDateClick?.(arg.dateStr);
        }}
      />
    </>
  );
};

export default CalendarView;
